import { WarningIcon } from "@chakra-ui/icons";
import { Text } from "@chakra-ui/react";
import { useNoticeDocumentTableDataRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DocumentDownloadLink } from "shared/components/link/DownloadDocumentLink";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RenderRowActionsFn } from "shared/components/table/data/DataTableTypes";
import { ErrorIcon } from "shared/icons/ErrorIcon";

export interface CapitalEventNoticesTableProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  isDraftEvent: boolean;
}

export const CapitalEventNoticesTable: React.FC<
  CapitalEventNoticesTableProps
> = ({ fundFamilyId, fundFamilyEventId, isDraftEvent }) => {
  const { t } = useTranslation(["capitalActivity", "common", "notices"]);

  const { data } = useNoticeDocumentTableDataRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => {
      const documentState =
        row.data?.cells["document_state"].value?.type === "text"
          ? row.data.cells["document_state"].value.text
          : "";
      if (
        row.data?.cells["document_id"].value?.type === "text" &&
        row.data.cells["document_id"].value.text &&
        documentState == DocumentStateMachineFieldsEnum.upload_succeeded
      ) {
        return {
          type: "buttons",
          actions: (
            <DocumentDownloadLink
              documentId={row.data.cells["document_id"].value.text}
            >
              <div>{t("common:download")}</div>
            </DocumentDownloadLink>
          ),
        };
      } else if (
        documentState == DocumentStateMachineFieldsEnum.upload_failed
      ) {
        return {
          type: "buttons",
          actions: (
            <>
              <ErrorIcon color="red.700" />
              <Text color="red.700">{t("notices:generationFailed")}</Text>
            </>
          ),
        };
      } else if (documentState == DocumentStateMachineFieldsEnum.out_of_date) {
        return {
          type: "buttons",
          actions: (
            <>
              <WarningIcon color="warning.800" />
              <Text color="warning.800">{t("notices:outOfDate")}</Text>
            </>
          ),
        };
      }
      return {
        type: "buttons",
        actions: <div />,
      };
    },
    [t],
  );

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>
          {isDraftEvent
            ? t("notices:download.title")
            : t("notices:emptyComplete.title")}
        </TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={data}
          renderRowActions={renderRowActions}
          emptyStateProps={
            isDraftEvent
              ? {
                  title: t("notices:empty.title"),
                  description: t("notices:empty.description"),
                }
              : {
                  title: t("notices:emptyComplete.tableTitle"),
                }
          }
        />
      </TableBoxBody>
    </TableBox>
  );
};
